import { useEffect, useId, useRef, useState } from 'react';
import { X, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiPatch } from '../utils/apiClient';
import { useModalFocus } from '../hooks/useModalFocus';

// Mirrors DISPLAY_NAME_MAX and the rules in backend/validation/schemas.js, so errors show inline
// before a round trip. The server is the authority; its field errors are shown inline too.
const DISPLAY_NAME_MAX = 60;

function validateName(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'Please enter your name.';
  if (trimmed.length > DISPLAY_NAME_MAX) return `Name must be ${DISPLAY_NAME_MAX} characters or fewer.`;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return "Name contains characters that aren't allowed.";
  return '';
}

// Edit Profile: name is editable (it's the name in household invites and emails); email is the
// sign-in and read-only for launch. A modal dialog using the shared useModalFocus behaviour (focus
// to the first interactive element, Tab trapped, Escape closes, focus returned on close). No toasts, ever: errors are
// inline and success is the app's green status box (same as Change Password), then it closes.
export function EditProfileDrawer({ isOpen, onClose, user, currentName = '', onSaved }) {
  const { user: authUser } = useAuth();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const closeTimerRef = useRef(null);
  const panelRef = useRef(null);
  const nameRef = useRef(null);
  const titleId = useId();
  const nameErrorId = useId();
  const emailHelpId = useId();

  // Email/password accounts change their email through support; Google/Apple manage it themselves.
  const providerIds = (authUser?.providerData || []).map((p) => p?.providerId).filter(Boolean);
  const emailHelp = providerIds.includes('password')
    ? 'Your email is your sign-in. Contact support to change it.'
    : providerIds.includes('google.com')
      ? 'Managed by your Google account.'
      : providerIds.includes('apple.com')
        ? 'Managed by your Apple account.'
        : 'Your email is your sign-in. Contact support to change it.';

  // Reset on open. Depends on isOpen only: the `user` prop is rebuilt on every parent render and
  // would otherwise wipe what's being typed.
  useEffect(() => {
    if (!isOpen) return undefined;
    setName(currentName);
    setNameError('');
    setFormError('');
    setSaving(false);
    setSuccessMessage('');
    return () => clearTimeout(closeTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useModalFocus({ isOpen, panelRef, onClose, canClose: !saving });

  if (!isOpen) return null;

  const handleNameChange = (event) => {
    const next = event.target.value;
    setName(next);
    // Once an error is showing, re-check as they type so it clears as soon as it's fixed.
    if (nameError) setNameError(validateName(next));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    const error = validateName(name);
    if (error) {
      setNameError(error);
      nameRef.current?.focus();
      return;
    }
    const trimmed = name.trim();
    if (trimmed === currentName.trim()) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const profile = await apiPatch('/user/me', { displayName: trimmed });
      // Every request copies the sign-in token's name into the account, so get a fresh token
      // carrying the new name before anything else is sent.
      try {
        await authUser?.reload?.();
        await authUser?.getIdToken?.(true);
      } catch (refreshErr) {
        console.warn('Name saved, but refreshing the sign-in token failed:', refreshErr);
      }
      onSaved?.(profile);
      // Show the confirmation in the drawer, then close. Long enough to read and to be announced.
      setSuccessMessage('Your name has been updated.');
      closeTimerRef.current = setTimeout(onClose, 2000);
    } catch (err) {
      const fieldMessage = (err?.details || []).find((d) => d?.path === 'displayName')?.message;
      if (err?.status === 400 && fieldMessage) {
        setNameError(fieldMessage);
        nameRef.current?.focus();
      } else if (err?.status === 429) {
        setFormError("You've made several account changes recently. Please wait a few minutes and try again.");
      } else {
        setFormError("We couldn't save your name. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} aria-hidden="true" />

      {/* Drawer */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="fixed bottom-0 left-0 right-0 bg-card text-foreground rounded-t-3xl z-50 focus:outline-none"
      >
        <div className="max-w-md mx-auto">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2" aria-hidden="true">
            <div className="w-10 h-1 bg-muted rounded-full" />
          </div>

          <div className="px-6 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 id={titleId} className="text-xl">Edit Profile</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="mpg-focus-ring w-11 h-11 -mr-2 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Always mounted so the confirmation is announced when it appears. */}
              <div role="status" aria-live="polite">
                {successMessage && (
                  <div className="mb-4 flex items-start gap-2 rounded-xl border border-[#BFDCCB] bg-[#EAF6EE] px-3 py-2 text-sm text-[#1F5D38]">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1F5D38]" aria-hidden="true" />
                    <span>{successMessage}</span>
                  </div>
                )}
              </div>

              {formError && (
                <p role="alert" className="mb-4 rounded-xl border border-[#D4183D] bg-[#FEF3F2] px-4 py-3 text-sm text-[#B42318]">
                  {formError}
                </p>
              )}

              {/* Name */}
              <div className="mb-4">
                <label htmlFor="profile-name" className="block text-foreground mb-2">
                  Name <span className="text-sm text-muted-foreground">(required)</span>
                </label>
                <input
                  ref={nameRef}
                  type="text"
                  id="profile-name"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={handleNameChange}
                  required
                  aria-required="true"
                  aria-invalid={nameError ? 'true' : 'false'}
                  aria-describedby={nameError ? nameErrorId : undefined}
                  placeholder="Your name"
                  className={`w-full px-4 py-3 bg-input rounded-xl text-foreground placeholder-muted-foreground border-2 ${
                    nameError ? 'border-[#D4183D]' : 'border-transparent'
                  }`}
                />
                {nameError && (
                  <p id={nameErrorId} className="mt-2 text-sm text-[#B42318]">
                    {nameError}
                  </p>
                )}
              </div>

              {/* Email: read-only for launch */}
              <div className="mb-6">
                <label htmlFor="profile-email" className="block text-foreground mb-1">
                  Email
                </label>
                {/* Read-only, so styled as plain text, not as a box you could type in. Still a labelled
                    input, so screen readers announce it as "Email, read only". */}
                <input
                  type="email"
                  id="profile-email"
                  value={user?.email || ''}
                  readOnly
                  aria-describedby={emailHelpId}
                  className="w-full bg-transparent px-0 py-1 border-0 rounded-md text-foreground cursor-default"
                />
                <p id={emailHelpId} className="mt-1 text-sm text-muted-foreground">
                  {emailHelp}
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="mpg-focus-ring flex-1 min-h-[48px] py-3 bg-white text-[#5F6B3F] font-medium rounded-full border-2 border-[#5F6B3F] hover:bg-[#F0F3EC] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !!successMessage}
                  aria-busy={saving || undefined}
                  className="mpg-focus-ring flex-1 min-h-[48px] py-3 bg-[#AFBB8B] text-[#1F2814] rounded-full hover:bg-[#9AA876] transition-colors disabled:cursor-wait disabled:opacity-70"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Save className="w-4 h-4" aria-hidden="true" />}
                    {saving ? 'Saving…' : 'Save'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
